import { useEffect, useRef, useState } from 'react';
import { CreditCard, Loader2, ShieldCheck } from 'lucide-react';
import { useLang } from '@/contexts/LanguageContext';
import api from '@/utils/api';

/**
 * PayPal JS SDK v5 – uses ONLY the `buttons` component (the most broadly
 * supported option). Smart Buttons render every funding source the buyer is
 * eligible for:
 *   - PayPal popup
 *   - Credit / Debit card (PayPal's guest checkout)
 *   - Apple Pay (Safari, eligible cards)
 *   - Google Pay (Chrome / Android, eligible cards)
 *   - Pay Later, Venmo
 *
 * Advanced Card Fields / native Apple Pay / native Google Pay require
 * merchant-specific approvals on the PayPal dashboard. If/when those are
 * approved, those components can be added here, but the Smart Button flow
 * already covers all four methods for any active PayPal merchant.
 *
 * A ref holds the latest `buildOrderPayload` so the current tip/gift/address
 * is always sent, even if the buttons were rendered earlier.
 */
export default function PaymentMethods({
  config,
  currency,
  buildOrderPayload,
  onSuccess,
  onError,
}) {
  const { t } = useLang();
  const [sdkReady, setSdkReady] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [initError, setInitError] = useState('');
  const buttonsRef = useRef(null);

  const payloadRef = useRef(buildOrderPayload);
  useEffect(() => { payloadRef.current = buildOrderPayload; }, [buildOrderPayload]);

  // Guest orders return a short-lived token that authorizes the capture.
  // Keep it in memory and sessionStorage (for the PayPal redirect flow).
  const guestTokenRef = useRef({});

  const createOrder = async () => {
    const payload = typeof payloadRef.current === 'function'
      ? await payloadRef.current()
      : payloadRef.current;
    const { data } = await api.post('/paypal/create-order', payload);
    if (data.guest_token) {
      guestTokenRef.current[data.id] = data.guest_token;
      try { sessionStorage.setItem(`tw_guest_${data.id}`, data.guest_token); } catch { /* noop */ }
    }
    return data.id;
  };

  const captureAndFinish = async (orderId) => {
    setProcessing(true);
    try {
      const guestToken = guestTokenRef.current[orderId];
      const { data } = await api.post(
        `/paypal/capture-order/${orderId}`, {},
        guestToken ? { headers: { 'X-Guest-Token': guestToken } } : undefined,
      );
      if (data.status === 'COMPLETED') {
        onSuccess?.({ orderId, ...data });
      } else {
        onError?.('Payment was not completed.');
      }
    } catch (err) {
      onError?.(err.response?.data?.detail || 'Failed to capture payment.');
    }
    setProcessing(false);
  };

  /* ------------------------------------------------------------------ */
  /* 1) Load classic PayPal JS SDK (minimal, always-compatible params)  */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!config?.enabled || !config?.client_id) return;

    const params = new URLSearchParams({
      'client-id': config.client_id,
      components: 'buttons',
      'enable-funding': 'paylater,venmo',
      currency: currency || 'USD',
      intent: 'capture',
    });
    const src = `https://www.paypal.com/sdk/js?${params.toString()}`;

    const existing = document.querySelector('script[data-paypal-sdk]');
    if (existing && existing.getAttribute('data-paypal-src') !== src) {
      existing.remove();
      try { delete window.paypal; } catch { /* noop */ }
    }
    if (window.paypal && existing?.getAttribute('data-paypal-src') === src) {
      setSdkReady(true);
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.setAttribute('data-paypal-sdk', '1');
    script.setAttribute('data-paypal-src', src);
    script.onload = () => setSdkReady(true);
    script.onerror = () => setInitError('Could not load PayPal SDK.');
    document.head.appendChild(script);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config?.client_id, config?.enabled, currency]);

  /* ------------------------------------------------------------------ */
  /* 2) Render stacked funding buttons                                   */
  /* ------------------------------------------------------------------ */
  useEffect(() => {
    if (!sdkReady || !window.paypal?.Buttons || !buttonsRef.current) return;
    buttonsRef.current.innerHTML = '';

    const paypalSDK = window.paypal;
    const FUNDING = paypalSDK.FUNDING || {};
    const desired = [
      FUNDING.PAYPAL,
      FUNDING.CARD,
      FUNDING.APPLEPAY,
      FUNDING.GOOGLEPAY,
      FUNDING.VENMO,
      FUNDING.PAYLATER,
    ].filter(Boolean);

    let anyEligible = false;
    desired.forEach((fundingSource) => {
      try {
        const btn = paypalSDK.Buttons({
          fundingSource,
          style: { layout: 'vertical', shape: 'pill', height: 48, tagline: false, label: 'pay' },
          createOrder,
          onApprove: (data) => captureAndFinish(data.orderID),
          onCancel: () => { setProcessing(false); onError?.('Payment cancelled.'); },
          onError: (err) => { setProcessing(false); onError?.(err?.message || 'PayPal error.'); },
        });
        if (!btn.isEligible || !btn.isEligible()) return;
        anyEligible = true;
        const container = document.createElement('div');
        container.style.marginBottom = '12px';
        container.setAttribute('data-testid', `paypal-funding-${fundingSource}`);
        buttonsRef.current.appendChild(container);
        btn.render(container).catch((err) => {
          // eslint-disable-next-line no-console
          console.error(`PayPal render failed (${fundingSource}):`, err);
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error(`PayPal button init error (${fundingSource}):`, err);
      }
    });

    if (!anyEligible) {
      // Nothing rendered — show a minimal PayPal button as a safety net
      try {
        paypalSDK.Buttons({
          style: { layout: 'vertical', shape: 'pill', height: 48, tagline: false, label: 'pay' },
          createOrder,
          onApprove: (data) => captureAndFinish(data.orderID),
          onCancel: () => { setProcessing(false); onError?.('Payment cancelled.'); },
          onError: (err) => { setProcessing(false); onError?.(err?.message || 'PayPal error.'); },
        }).render(buttonsRef.current);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('PayPal fallback render error:', err);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sdkReady]);

  if (!config?.enabled) {
    return (
      <div className="glass-card rounded-xl p-6 text-center" data-testid="payment-disabled">
        <ShieldCheck size={32} className="text-text-muted/30 mx-auto mb-3" />
        <p className="text-text-muted font-body text-sm">{t('checkout.paymentConfiguring')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative" data-testid="payment-methods">
      {processing && (
        <div
          className="absolute inset-0 z-20 rounded-xl bg-cream-white/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2"
          data-testid="payment-processing-overlay"
        >
          <Loader2 size={28} className="animate-spin text-sacred-gold" />
          <p className="text-heading font-body text-sm">{t('checkout.finalizing')}</p>
        </div>
      )}

      <div className="glass-card rounded-xl p-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-sacred-gold/10 flex items-center justify-center">
          <CreditCard size={20} className="text-sacred-gold" />
        </div>
        <div>
          <h3 className="text-heading font-body text-sm font-semibold">{t('checkout.choosePayment')}</h3>
          <p className="text-text-muted text-xs font-body">
            {sdkReady ? t('checkout.securedBy') : t('checkout.loadingSecure')}
          </p>
        </div>
      </div>

      {initError && (
        <div className="glass-card rounded-xl p-4 text-center text-red-400 text-sm font-body" data-testid="payment-init-error">
          {initError}
        </div>
      )}

      {!sdkReady && !initError && (
        <div className="glass-card rounded-xl p-6 flex items-center justify-center gap-2" data-testid="payment-loading">
          <Loader2 size={16} className="animate-spin text-sacred-gold" />
          <span className="text-text-muted font-body text-sm">{t('checkout.loadingOptions')}</span>
        </div>
      )}

      <div
        ref={buttonsRef}
        className="glass-card rounded-xl p-5"
        data-testid="paypal-buttons-container"
      />

      <div className="flex items-start gap-3 px-4 py-3 rounded-lg bg-accent-success/5 border border-accent-success/10">
        <ShieldCheck size={16} className="text-accent-success mt-0.5 flex-shrink-0" />
        <p className="text-text-muted font-body text-xs leading-relaxed">
          {t('checkout.secureNote')}
        </p>
      </div>
    </div>
  );
}
